import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelectOption } from '@hqo/multi-select';
import { useIntl } from 'react-intl';

import { setHostFilter } from '../../../store/visits/actions';
import { getHostFilter, getHostOptions } from '../../../store/visits/selectors';
import { usePaginatedHosts } from '../../../store/hosts/hooks';

export interface UseHostFilterReturn {
  options: MultiSelectOption[];
  selectedOptions: MultiSelectOption[];
  hostLabel: string;
  selectedCount: number;
  handleChange: (selected: MultiSelectOption[]) => void;
  isSet: boolean;
  searchValue: string;
  handleSearch: (value: string) => void;
  isLoading: boolean;
  onFetchMore: () => void;
  isFetchingMore: boolean;
  hasMore: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;

export const useHostFilter = (): UseHostFilterReturn => {
  const dispatch = useDispatch();
  const value = useSelector(getHostFilter);
  const storedOptions = useSelector(getHostOptions);
  const { formatMessage } = useIntl();
  const hostLabel = formatMessage({ id: 'labels.host', defaultMessage: 'Host' });
  const { data: hostsData, loading, loadingMore, hasMore, loadMore, search } = usePaginatedHosts();
  const [searchValue, setSearchValue] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const options: MultiSelectOption[] = useMemo((): MultiSelectOption[] => {
    if (!hostsData) {
      return [];
    }

    return hostsData.map((host) => {
      const identifier = host.id ?? host.uuid ?? host.venue_id;
      return {
        value: String(identifier),
        label: `${host.first_name} ${host.last_name}`.trim() || host.email || '',
      };
    });
  }, [hostsData]);

  const handleChange = useCallback(
    (selected: MultiSelectOption[]) => {
      dispatch(setHostFilter(selected));
    },
    [dispatch],
  );

  const handleSearch = useCallback(
    (query: string) => {
      setSearchValue(query);

      // Debounce the API search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        search(query);
      }, SEARCH_DEBOUNCE_MS);
    },
    [search],
  );

  // Derive selectedOptions from the persisted hostOptions so the chosen host
  // remains visible even when search filters it out of `options` (VIS-766)
  // and after page reload with pinned filters (VIS-767).
  const selectedOptions = useMemo(() => {
    const fromOptions = options.filter((option) => value.includes(option.value as string));
    const seen = new Set(fromOptions.map((option) => option.value as string));
    const fromStore = storedOptions.filter(
      (option) => value.includes(option.value as string) && !seen.has(option.value as string),
    );
    return [...fromOptions, ...fromStore];
  }, [options, storedOptions, value]);

  const selectedCount = useMemo(() => value.length, [value.length]);

  return {
    options,
    selectedOptions,
    hostLabel,
    selectedCount,
    handleChange,
    isSet: selectedOptions.length > 0,
    searchValue,
    handleSearch,
    isLoading: loading,
    onFetchMore: loadMore,
    isFetchingMore: loadingMore,
    hasMore,
  };
};
